import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Eye, EyeOff, Check, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordFromInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation rules
  const rules = {
    minLength: password.length >= 6,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    passwordsMatch: password === confirmPassword && password.length > 0
  };

  const allRulesPassed = Object.values(rules).every(rule => rule);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRulesPassed) {
      setError('Bitte erfüllen Sie alle Passwort-Anforderungen');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/set-password-from-invitation`, {
        token,
        password
      });
      
      setSuccess(true);
      
      // Redirect to login after 8 seconds (more time to read the message)
      setTimeout(() => {
        navigate('/login');
      }, 8000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Setzen des Passworts');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ungültiger Link</h2>
          <p className="text-gray-600">Dieser Einladungslink ist ungültig.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 max-w-lg text-center shadow-lg">
          <CheckCircle className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Fast geschafft! 🎉
          </h2>
          <div className="text-left bg-white rounded-lg p-6 mb-6 border border-blue-100">
            <p className="text-gray-800 mb-4 leading-relaxed">
              <strong className="text-blue-600">Zu Ihrer Sicherheit</strong> haben wir Ihnen soeben einen <strong>Bestätigungslink</strong> an Ihre E-Mail-Adresse gesendet.
            </p>
            <p className="text-gray-800 mb-4 leading-relaxed">
              Bitte <strong>verifizieren Sie Ihr Konto</strong>, um AllFlowX vollumfänglich nutzen zu können.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <span className="text-lg">💡</span>
                <span>
                  <strong>Hinweis:</strong> Prüfen Sie auch Ihren <strong>Spam-Ordner</strong>, falls Sie die E-Mail nicht finden.
                </span>
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Sie werden automatisch zur Anmeldung weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 sm:p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <img 
            src="/allflowx-horizontal-light.png" 
            alt="AllFlowX" 
            className="h-24 w-auto object-contain mx-auto mb-4 dark:hidden"
          />
          <img 
            src="/allflowx-horizontal-dark.png" 
            alt="AllFlowX" 
            className="h-24 w-auto object-contain mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Passwort festlegen
          </h1>
          <p className="text-sm text-gray-600">
            Bitte legen Sie ein sicheres Passwort für Ihren Account fest.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Neues Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                placeholder="Passwort eingeben"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passwort bestätigen
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Passwort wiederholen"
            />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Passwort-Anforderungen:
            </p>
            <ul className="space-y-1 text-sm">
              <li className={`flex items-center gap-2 ${rules.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                {rules.minLength ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                Mindestens 6 Zeichen
              </li>
              <li className={`flex items-center gap-2 ${rules.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                {rules.hasUppercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                Mindestens 1 Großbuchstabe
              </li>
              <li className={`flex items-center gap-2 ${rules.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                {rules.hasNumber ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                Mindestens 1 Zahl
              </li>
              <li className={`flex items-center gap-2 ${rules.hasSpecial ? 'text-green-600' : 'text-gray-500'}`}>
                {rules.hasSpecial ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                Mindestens 1 Sonderzeichen (!@#$%^&*...)
              </li>
              <li className={`flex items-center gap-2 ${rules.passwordsMatch ? 'text-green-600' : 'text-gray-500'}`}>
                {rules.passwordsMatch ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                Passwörter stimmen überein
              </li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading || !allRulesPassed}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'Wird gespeichert...' : 'Passwort festlegen'}
          </button>
        </form>
      </div>
    </div>
  );
}
